import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  RefreshCw, UserMinus, MapPin, Clock, Users, Plus, Trash2, X,
  AlertTriangle, CheckCircle2, ArrowRightLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getBackendUrl } from '../utils/config';

const API_URL = getBackendUrl();

const TIPOS_ALTERACAO = {
  ausencia: { label: 'Marcar Ausência', icon: UserMinus, color: 'text-red-600 bg-red-50 border-red-200' },
  mudanca_local: { label: 'Mudar Local', icon: MapPin, color: 'text-blue-600 bg-blue-50 border-blue-200' },
  mudanca_turno: { label: 'Mudar Turno/Hora', icon: Clock, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  substituicao: { label: 'Substituir', icon: ArrowRightLeft, color: 'text-purple-600 bg-purple-50 border-purple-200' },
  turno_extra: { label: 'Turno Extra', icon: Plus, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
};

const LOCAIS = ['Café', 'Loja Sátão', 'Loja Avelal'];

const AlteracaoEscalaDia = ({ data, grelhaDia, colaboradores, onAlteracaoSaved, isOpen, onClose }) => {
  const [alteracoes, setAlteracoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formTipo, setFormTipo] = useState('');
  const [selectedColab, setSelectedColab] = useState(null);
  const [formData, setFormData] = useState({
    novo_local: '',
    nova_entrada: '',
    nova_saida: '',
    substituto_id: '',
    substituto_nome: '',
    motivo: '',
  });

  const token = localStorage.getItem('token');

  const loadAlteracoes = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/alteracoes-dia`, {
        params: { data },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlteracoes(res.data);
    } catch (err) {
      console.error('Erro ao carregar alterações:', err);
    }
  }, [data, token]);

  useEffect(() => {
    if (isOpen) loadAlteracoes();
  }, [isOpen, data, loadAlteracoes]);

  // Lista de todos os colaboradores na grelha (Manhã + Tarde)
  const colabsGrelha = [
    ...(grelhaDia?.turnos?.['Manhã'] || []),
    ...(grelhaDia?.turnos?.['Tarde'] || []),
  ];

  // Lista para dropdown de substitutos (todos os colaboradores, incluindo os que não estão na grelha)
  const todosColabs = colaboradores || [];

  const iniciarAlteracao = (colab, tipo) => {
    setSelectedColab(colab);
    setFormTipo(tipo);
    setFormData({
      novo_local: tipo === 'mudanca_local' ? '' : colab.local || '',
      nova_entrada: tipo === 'mudanca_turno' ? '' : '',
      nova_saida: tipo === 'mudanca_turno' ? '' : '',
      substituto_id: '',
      substituto_nome: '',
      motivo: '',
    });
    setShowForm(true);
  };

  const iniciarTurnoExtra = () => {
    setSelectedColab(null);
    setFormTipo('turno_extra');
    setFormData({
      colaborador_id: '',
      colaborador_nome: '',
      novo_local: '',
      nova_entrada: '',
      nova_saida: '',
      substituto_id: '',
      substituto_nome: '',
      motivo: '',
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        data,
        tipo_alteracao: formTipo,
        colaborador_id: formTipo === 'turno_extra' ? formData.colaborador_id : selectedColab?.colaborador_id,
        colaborador_nome: formTipo === 'turno_extra' ? formData.colaborador_nome : selectedColab?.colaborador_nome,
        original_local: selectedColab?.local || null,
        original_entrada: selectedColab?.entrada || null,
        original_saida: selectedColab?.saida || null,
        original_turno: selectedColab?.turno || null,
        motivo: formData.motivo,
      };

      if (formTipo === 'mudanca_local') {
        if (!formData.novo_local) { toast.error('Selecione o novo local'); setLoading(false); return; }
        payload.novo_local = formData.novo_local;
      } else if (formTipo === 'mudanca_turno') {
        if (!formData.nova_entrada || !formData.nova_saida) { toast.error('Preencha entrada e saída'); setLoading(false); return; }
        payload.nova_entrada = formData.nova_entrada;
        payload.nova_saida = formData.nova_saida;
        if (formData.novo_local) payload.novo_local = formData.novo_local;
      } else if (formTipo === 'substituicao') {
        if (!formData.substituto_id) { toast.error('Selecione o substituto'); setLoading(false); return; }
        payload.substituto_id = formData.substituto_id;
        payload.substituto_nome = formData.substituto_nome;
      } else if (formTipo === 'turno_extra') {
        if (!formData.colaborador_id || !formData.novo_local || !formData.nova_entrada || !formData.nova_saida) {
          toast.error('Preencha todos os campos do turno extra');
          setLoading(false);
          return;
        }
        payload.novo_local = formData.novo_local;
        payload.nova_entrada = formData.nova_entrada;
        payload.nova_saida = formData.nova_saida;
      }

      await axios.post(`${API_URL}/api/alteracoes-dia`, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      toast.success(`Alteração registada: ${TIPOS_ALTERACAO[formTipo]?.label}`);
      setShowForm(false);
      setSelectedColab(null);
      await loadAlteracoes();
      if (onAlteracaoSaved) onAlteracaoSaved();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao registar alteração';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover esta alteração?')) return;
    try {
      await axios.delete(`${API_URL}/api/alteracoes-dia/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Alteração removida');
      await loadAlteracoes();
      if (onAlteracaoSaved) onAlteracaoSaved();
    } catch (err) {
      toast.error('Erro ao remover alteração');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="alteracao-escala-modal">
      <div className="bg-card rounded-lg max-w-3xl w-full max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-amber-600" />
            <h2 className="text-lg font-semibold">Alterar Escala do Dia</h2>
            <span className="text-sm text-muted-foreground ml-2">
              {new Date(data + 'T00:00:00').toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long' })}
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-alteracao-modal">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          {/* Alterações já registadas */}
          {alteracoes.length > 0 && (
            <Card className="border-amber-200 bg-amber-50/50" data-testid="alteracoes-registadas">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="h-4 w-4" />
                  Alterações Registadas Hoje ({alteracoes.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-1.5">
                {alteracoes.map((alt) => {
                  const tipoInfo = TIPOS_ALTERACAO[alt.tipo_alteracao] || {};
                  const Icon = tipoInfo.icon || AlertTriangle;
                  return (
                    <div key={alt.id} className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${tipoInfo.color || 'bg-gray-50 border-gray-200'}`} data-testid={`alteracao-item-${alt.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon className="h-4 w-4 shrink-0" />
                        <div className="min-w-0">
                          <span className="font-semibold">{alt.colaborador_nome}</span>
                          <span className="text-xs ml-2 opacity-75">
                            {alt.tipo_alteracao === 'ausencia' && 'Ausente'}
                            {alt.tipo_alteracao === 'mudanca_local' && `→ ${alt.novo_local}`}
                            {alt.tipo_alteracao === 'mudanca_turno' && `→ ${alt.nova_entrada}-${alt.nova_saida}`}
                            {alt.tipo_alteracao === 'substituicao' && `→ ${alt.substituto_nome}`}
                            {alt.tipo_alteracao === 'turno_extra' && `Extra: ${alt.nova_entrada}-${alt.nova_saida} @ ${alt.novo_local}`}
                          </span>
                          {alt.motivo && <p className="text-xs opacity-60 truncate">{alt.motivo}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs opacity-50">{alt.criado_por_nome}</span>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => handleDelete(alt.id)} data-testid={`delete-alteracao-${alt.id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Formulário de nova alteração */}
          {showForm ? (
            <Card className="border-primary/30" data-testid="form-nova-alteracao">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  {formTipo && TIPOS_ALTERACAO[formTipo]?.icon && React.createElement(TIPOS_ALTERACAO[formTipo].icon, { className: 'h-4 w-4' })}
                  {formTipo === 'turno_extra' ? 'Adicionar Turno Extra' : `${TIPOS_ALTERACAO[formTipo]?.label || ''}: ${selectedColab?.colaborador_nome || ''}`}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                {/* Turno Extra: selecionar colaborador */}
                {formTipo === 'turno_extra' && (
                  <div>
                    <label className="block text-xs font-medium mb-1">Colaborador</label>
                    <select
                      className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                      value={formData.colaborador_id}
                      onChange={(e) => {
                        const c = todosColabs.find(x => x.id === e.target.value);
                        setFormData({ ...formData, colaborador_id: e.target.value, colaborador_nome: c?.nome || '' });
                      }}
                      data-testid="select-turno-extra-colab"
                    >
                      <option value="">Selecionar...</option>
                      {todosColabs.map(c => (
                        <option key={c.id} value={c.id}>{c.nome}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Mudança de local */}
                {(formTipo === 'mudanca_local' || formTipo === 'mudanca_turno' || formTipo === 'turno_extra') && (
                  <div>
                    <label className="block text-xs font-medium mb-1">
                      {formTipo === 'mudanca_local' ? 'Novo Local' : 'Local'}
                    </label>
                    <select
                      className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                      value={formData.novo_local}
                      onChange={(e) => setFormData({ ...formData, novo_local: e.target.value })}
                      data-testid="select-novo-local"
                    >
                      <option value="">{formTipo === 'mudanca_turno' ? 'Manter actual' : 'Selecionar...'}</option>
                      {LOCAIS.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>
                )}

                {/* Mudança de turno/hora */}
                {(formTipo === 'mudanca_turno' || formTipo === 'turno_extra') && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Entrada</label>
                      <input
                        type="time"
                        className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                        value={formData.nova_entrada}
                        onChange={(e) => setFormData({ ...formData, nova_entrada: e.target.value })}
                        data-testid="input-nova-entrada"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Saída</label>
                      <input
                        type="time"
                        className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                        value={formData.nova_saida}
                        onChange={(e) => setFormData({ ...formData, nova_saida: e.target.value })}
                        data-testid="input-nova-saida"
                      />
                    </div>
                  </div>
                )}

                {/* Substituição */}
                {formTipo === 'substituicao' && (
                  <div>
                    <label className="block text-xs font-medium mb-1">Substituto</label>
                    <select
                      className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                      value={formData.substituto_id}
                      onChange={(e) => {
                        const c = todosColabs.find(x => x.id === e.target.value);
                        setFormData({ ...formData, substituto_id: e.target.value, substituto_nome: c?.nome || '' });
                      }}
                      data-testid="select-substituto"
                    >
                      <option value="">Selecionar substituto...</option>
                      {todosColabs
                        .filter(c => c.id !== selectedColab?.colaborador_id)
                        .map(c => (
                          <option key={c.id} value={c.id}>{c.nome}</option>
                        ))
                      }
                    </select>
                  </div>
                )}

                {/* Motivo */}
                <div>
                  <label className="block text-xs font-medium mb-1">Motivo / Nota (opcional)</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                    placeholder="Ex: Troca com colega, falta justificada..."
                    value={formData.motivo}
                    onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
                    data-testid="input-motivo"
                  />
                </div>

                {/* Botões */}
                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    onClick={handleSubmit}
                    disabled={loading}
                    data-testid="btn-confirmar-alteracao"
                    className="bg-primary hover:bg-primary/90"
                  >
                    {loading ? 'A guardar...' : 'Confirmar'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setShowForm(false); setSelectedColab(null); }}
                    data-testid="btn-cancelar-alteracao"
                  >
                    Cancelar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {/* Lista de colaboradores do dia — acções rápidas */}
          <Card data-testid="lista-colabs-alteracao">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Turnos do Dia
                </CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  onClick={iniciarTurnoExtra}
                  data-testid="btn-turno-extra"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Turno Extra
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {colabsGrelha.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">Sem turnos planeados para este dia</p>
              ) : (
                <div className="space-y-1.5">
                  {colabsGrelha.map((item, idx) => {
                    const isOverridden = alteracoes.some(a => a.colaborador_id === item.colaborador_id);
                    return (
                      <div
                        key={`${item.colaborador_id}-${idx}`}
                        className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${
                          isOverridden ? 'bg-amber-50/50 border-amber-200 opacity-60' :
                          item.override ? 'bg-blue-50/50 border-blue-200' :
                          'bg-white border-gray-200'
                        }`}
                        data-testid={`colab-alteracao-${item.colaborador_id}`}
                      >
                        <div className="min-w-0">
                          <p className="font-semibold truncate text-sm">
                            {item.colaborador_nome}
                            {item.override && <span className="ml-1 text-xs text-blue-600">(alterado)</span>}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {item.turno} &middot; {item.local} &middot; {item.entrada}–{item.saida}
                          </p>
                        </div>
                        {!isOverridden && (
                          <div className="flex items-center gap-1 shrink-0">
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-600 hover:bg-red-50"
                              onClick={() => iniciarAlteracao(item, 'ausencia')} title="Marcar ausência"
                              data-testid={`btn-ausencia-${item.colaborador_id}`}>
                              <UserMinus className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-blue-600 hover:bg-blue-50"
                              onClick={() => iniciarAlteracao(item, 'mudanca_local')} title="Mudar local"
                              data-testid={`btn-local-${item.colaborador_id}`}>
                              <MapPin className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-amber-600 hover:bg-amber-50"
                              onClick={() => iniciarAlteracao(item, 'mudanca_turno')} title="Mudar turno/hora"
                              data-testid={`btn-turno-${item.colaborador_id}`}>
                              <Clock className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-purple-600 hover:bg-purple-50"
                              onClick={() => iniciarAlteracao(item, 'substituicao')} title="Substituir"
                              data-testid={`btn-substituir-${item.colaborador_id}`}>
                              <ArrowRightLeft className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                        {isOverridden && (
                          <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" /> Alterado
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AlteracaoEscalaDia;
